import { Helmet } from "react-helmet-async";

const DEFAULT_TITLE = "AfricaArtMasters · Curated African Contemporary Art";
const DEFAULT_DESC =
  "A curated portal where collectors meet contemporary African masters. Browse paintings, mixed media and sculpture from Harare, Johannesburg, Lusaka and beyond — direct from the artist.";
const DEFAULT_OG_IMAGE =
  "https://customer-assets-4nw71qhi.emergentagent.net/job_africa-art-masters/artifacts/5y87baed_aam-logo%20%281%29.jpg";

// Origin used to build absolute canonical URLs. window not available during
// SSR-style build steps, so guard. Canonical is the `www` host because the
// apex domain 301-redirects to it on production.
const getOrigin = () => {
  if (typeof window !== "undefined" && window.location) return window.location.origin;
  return "https://www.africaartmasters.com";
};

/**
 * <Seo> — page-level SEO controller.
 *
 * Drop into any page component to override <title>, meta description,
 * canonical URL, Open Graph + Twitter card image, and inject one or more
 * JSON-LD blocks for structured data (Product, Person, Article, Event,
 * BreadcrumbList, etc.).
 *
 * All props are optional. Anything you omit falls back to sensible site-wide
 * defaults set in /public/index.html so social shares of the site root still
 * look right.
 */
export default function Seo({
  title,
  description,
  image,
  imageAlt,
  type = "website",
  path,
  jsonLd,
  noindex = false,
}) {
  const fullTitle = title ? `${title} · AfricaArtMasters` : DEFAULT_TITLE;
  const desc = description || DEFAULT_DESC;
  const url = `${getOrigin()}${path || (typeof window !== "undefined" ? window.location.pathname : "")}`;
  const ogImage = image || DEFAULT_OG_IMAGE;
  const ogImageAlt = imageAlt || "AfricaArtMasters";

  const blocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="AfricaArtMasters" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={ogImageAlt} />

      {blocks.map((block) => {
        // Stable key derived from the block's @type + a short slug of its
        // identifying field (so re-renders match by content, not array index).
        const t = block?.["@type"] || "ld";
        const ident =
          block?.url ||
          block?.mainEntityOfPage ||
          block?.name ||
          block?.headline ||
          (Array.isArray(block?.itemListElement) ? `bc-${block.itemListElement.length}` : "");
        const k = `${t}:${String(ident).slice(0, 64)}`;
        return (
          <script key={k} type="application/ld+json">
            {JSON.stringify(block)}
          </script>
        );
      })}
    </Helmet>
  );
}

/* ── JSON-LD builders (small helpers so pages stay tidy) ─────────────── */

export const organizationLd = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "AfricaArtMasters",
  url: getOrigin(),
  logo: `${getOrigin()}/branding/logo-emblem-512.png`,
  sameAs: [
    "https://www.instagram.com/africaartmasters",
    "https://twitter.com/africaartmasters",
    "https://x.com/africaartmasters",
    "https://www.facebook.com/africaartmasters",
  ],
});

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${getOrigin()}${it.path}`,
  })),
});

export const productLd = ({ artwork, artist, url, image }) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: artwork.title,
  description: artwork.description || `${artwork.medium || "Original artwork"} by ${artwork.artist_name || "African master"}`,
  image: image || artwork.image_url,
  brand: { "@type": "Person", name: artwork.artist_name || artist?.name },
  category: artwork.medium,
  url,
  offers: {
    "@type": "Offer",
    priceCurrency: artwork.currency || "USD",
    price: String(artwork.price ?? ""),
    availability:
      artwork.status === "sold"
        ? "https://schema.org/SoldOut"
        : "https://schema.org/InStock",
    url,
  },
});

export const personLd = ({ artist, url }) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  name: artist.name,
  description: artist.bio,
  image: artist.avatar_url,
  nationality: artist.country,
  url,
  jobTitle: "Contemporary African Artist",
});

export const articleLd = ({ article, url, image }) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: article.title,
  description: article.excerpt,
  image: image || article.cover_image,
  datePublished: article.published_at || article.created_at,
  dateModified: article.updated_at,
  author: { "@type": "Person", name: article.author_name },
  publisher: {
    "@type": "Organization",
    name: "AfricaArtMasters",
    logo: { "@type": "ImageObject", url: `${getOrigin()}/favicon.svg` },
  },
  mainEntityOfPage: url,
});

export const eventLd = ({ event, url }) => ({
  "@context": "https://schema.org",
  "@type": "Event",
  name: event.title,
  description: event.description,
  image: event.cover_image,
  startDate: event.start_at,
  endDate: event.end_at,
  eventStatus: "https://schema.org/EventScheduled",
  eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
  location: event.location
    ? { "@type": "Place", name: event.location, address: event.location }
    : undefined,
  organizer: { "@type": "Person", name: event.organizer_name },
  url: event.external_url || url,
});
